import { cn } from '../../../lib/cn';
import { TICKET_PRIORITIES, TICKET_PRIORITY_LABELS, type TicketPriority } from '../tickets.schema';

import { PRIORITY_ACTIVE, PRIORITY_ICON_TINT, PRIORITY_ICONS } from './TicketPriorityBadge';

type Props = {
    value: TicketPriority;
    onChange: (priority: TicketPriority) => void;
};

// Priority as a colour-coded button group instead of a dropdown — every option
// is visible at once and the selected one fills with its own loud colour, so
// "how urgent is this" is a single tap with an unmissable visual answer. Far
// friendlier than a <select> for someone who isn't comfortable with computers.
export function PriorityPicker({ value, onChange }: Props) {
    return (
        <div role="radiogroup" aria-label="Prioritate" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {TICKET_PRIORITIES.map((p) => {
                const Icon = PRIORITY_ICONS[p];
                const selected = p === value;
                return (
                    <button
                        key={p}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => onChange(p)}
                        className={cn(
                            'inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-solid text-base font-semibold transition-all',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500',
                            selected
                                ? cn('scale-[1.02]', PRIORITY_ACTIVE[p])
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700',
                        )}
                    >
                        <Icon
                            aria-hidden="true"
                            className={cn('h-5 w-5 shrink-0', !selected && PRIORITY_ICON_TINT[p])}
                        />
                        {TICKET_PRIORITY_LABELS[p]}
                    </button>
                );
            })}
        </div>
    );
}
